import './Select.scss'
import { useId, useMemo, useRef, useState, type ComponentProps } from 'react'
import { MdOutlineExpandMore } from 'react-icons/md'
import { clx } from '../clx'
import { useSelectListbox, type ListboxItem } from '../SelectListbox'

export interface SelectOption {
  value: string
  label: string
  disabled?: boolean
}

/** Rendered as a labelled group in the listbox. */
export interface SelectGroup {
  label: string
  options: SelectOption[]
}

export interface SelectProps extends Omit<
  ComponentProps<'button'>,
  'value' | 'defaultValue' | 'onChange' | 'children' | 'type' | 'role'
> {
  options: (SelectOption | SelectGroup)[]
  value?: string
  defaultValue?: string
  onChange?: (value: string) => void
  /** Text of an empty first option (value `''`); `''` gives a blank one, like the portal's `emptyText=''`. */
  placeholder?: string
  size?: 'sm' | 'md'
}

const isGroup = (item: SelectOption | SelectGroup): item is SelectGroup => 'options' in item

/**
 * A single select: the WAI-ARIA select-only combobox, with the same listbox
 * popup as PillSelect (`useSelectListbox`), in a form control's box. Spread
 * Field's control props onto it; `ref` reaches the button, so a form can
 * focus it.
 */
export function Select({
  options,
  value,
  defaultValue,
  onChange,
  placeholder,
  size = 'md',
  id,
  className,
  disabled,
  ref,
  ...rest
}: SelectProps) {
  const generated = useId()
  const buttonId = id ?? `select-${generated}`
  const buttonRef = useRef<HTMLButtonElement | null>(null)

  const items = useMemo(() => {
    const list: ListboxItem[] = []
    if (placeholder !== undefined) {
      // An empty placeholder (react-admin's emptyText='') still needs a name.
      list.push({ value: '', label: placeholder, ...(placeholder === '' && { ariaLabel: 'None' }) })
    }
    for (const item of options) {
      if (isGroup(item)) list.push(...item.options.map((o) => ({ ...o, group: item.label })))
      else list.push(item)
    }
    return list
  }, [options, placeholder])

  // Like a native select, uncontrolled starts on the first option.
  const [ownValue, setOwnValue] = useState(() => defaultValue ?? items[0]?.value ?? '')
  const current = value ?? ownValue
  const selected = items.find((item) => item.value === current)

  const listbox = useSelectListbox({
    anchorRef: buttonRef,
    id: buttonId,
    items,
    value: current,
    align: 'start',
    disabled,
    onPick: (next) => {
      if (value === undefined) setOwnValue(next)
      onChange?.(next)
    }
  })

  return (
    <div className={clx('select', size !== 'md' && `select--is-${size}`, className)}>
      <button
        {...rest}
        ref={(node) => {
          buttonRef.current = node
          if (typeof ref === 'function') ref(node)
          else if (ref) ref.current = node
        }}
        id={buttonId}
        type="button"
        role="combobox"
        // The current value, as on a native select (and for `name`).
        value={current}
        {...listbox.triggerProps}
        disabled={disabled}
        className="select__input"
      >
        <span
          className="select__value"
          data-placeholder={(selected && selected.value === '') || undefined}
        >
          {selected?.label}
        </span>
        <span className="select__chevron" aria-hidden="true">
          <MdOutlineExpandMore />
        </span>
      </button>
      {listbox.popup}
    </div>
  )
}
